import { NextPageContext } from 'next'
import { ApiResponse } from '../../types'
import {
  TagData,
  CompanyData,
  FundingRoundData,
  OverseasCompanySectionData,
} from '../../types/api'
import { callAuthAPI, basePath, callAPI } from './common'
import { toQueryParams } from '../url'
import { toLowerCase } from '@/lib/roundtype'
import { DateRange } from '@/components/atoms/search/DateRangeFilterSelect'
import { convertToDateString } from '../date'
import { printError } from '../log'

export const fetchTagPage = async (
  ctx: NextPageContext,
  slugOrName: string
) => {
  let tag
  try {
    tag = await callAuthAPI<ApiResponse<Required<TagData>>>(
      ctx,
      `${basePath}/pages/tags/${encodeSlugOrName(slugOrName)}`
    )
  } catch (e) {
    printError(e)
  }
  return tag ? tag.data : null
}

export const fetchTagCompanies = async (
  slugOrName: string,
  options: { offset?: number } = {}
) => {
  let res
  const q = toQueryParams(options)
  try {
    res = await callAPI<{ data: CompanyData[]; count: number }>(
      `${basePath}/tags/${encodeSlugOrName(slugOrName)}/companies?${q}`,
      { credentials: 'include' }
    )
  } catch (e) {
    printError(e)
  }
  return res
}

export const fetchTagOverseasCompanies = async (
  slug: string,
  offset: number,
  condition: { roundType: string[]; lastFundingOn: DateRange }
) => {
  let res
  let roundType = condition.roundType
    .map((type) => (type ? toLowerCase(type) : ''))
    .join()
  let lastFundraisedFrom = condition.lastFundingOn[0]
    ? convertToDateString(condition.lastFundingOn[0])
    : ''
  let lastFundraisedTo = condition.lastFundingOn[1]
    ? convertToDateString(condition.lastFundingOn[1])
    : ''
  try {
    res = await callAPI<{ data: OverseasCompanySectionData[]; count: number }>(
      `${basePath}/tags/${slug}/overseas_companies?offset=${offset}&roundType=${roundType}&lastFundraisedFrom=${lastFundraisedFrom}&lastFundraisedTo=${lastFundraisedTo}`,
      { credentials: 'include' }
    )
  } catch (e) {
    printError(e)
  }
  return res
}

export const fetchTagFundraiseRounds = async (
  slugOrName: string,
  options: { offset?: number } = {}
) => {
  let res
  const q = toQueryParams(options)
  try {
    res = await callAPI<{
      data: Array<Required<FundingRoundData>>
      count: number
    }>(
      `${basePath}/tags/${encodeSlugOrName(slugOrName)}/fundraise_rounds?${q}`,
      { credentials: 'include' }
    )
  } catch (e) {
    printError(e)
  }
  return res
}

export const fetchTagEventRounds = async (
  slugOrName: string,
  options: { offset?: number } = {}
) => {
  let res
  const q = toQueryParams(options)
  try {
    res = await callAPI<{
      data: Array<Required<FundingRoundData>>
      count: number
    }>(`${basePath}/tags/${encodeSlugOrName(slugOrName)}/event_rounds?${q}`, {
      credentials: 'include',
    })
  } catch (e) {
    printError(e)
  }
  return res
}

const encodeSlugOrName = (slugOrName: string) => {
  return isEncoded(slugOrName) ? slugOrName : encodeURIComponent(slugOrName)
}

const isEncoded = (text: string) => {
  return text !== decodeURIComponent(text)
}

export const getTagFollowingStatus = async (slugOrName: string) => {
  return callAPI<{ following: boolean }>(
    `${basePath}/tags/${slugOrName}/following`
  )
}

export const followTag = async (slugOrName: string) => {
  return callAPI<{ following: boolean }>(
    `${basePath}/tags/${slugOrName}/follow`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
}

export const unfollowTag = async (slugOrName: string) => {
  return callAPI<{ following: boolean }>(
    `${basePath}/tags/${slugOrName}/unfollow`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
}
